import { EventStatus } from "../types/Filter";

export type LiveEventMarkerStatus = "upcoming" | "active" | "endingSoon" | "completed" | "unknown";

const ENDING_SOON_WINDOW_MS = 60 * 60 * 1000;

type LiveEventTiming = {
  status?: string | null;
  start_time?: Date | string | null;
  end_time?: Date | string | null;
};

const toTimestamp = (value: Date | string | null | undefined): number | null => {
  if (!value) return null;

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const toFallbackStatus = (status: string | null | undefined): LiveEventMarkerStatus => {
  switch (status?.trim().toLowerCase().replace(/[_-]+/g, " ")) {
    case "upcoming":
    case "starting soon":
      return "upcoming";
    case "active":
      return "active";
    case "ending soon":
      return "endingSoon";
    case "completed":
    case "ended":
      return "completed";
    default:
      return "unknown";
  }
};

export const getLiveEventMarkerStatus = (
  event: LiveEventTiming,
  now: Date = new Date(),
): LiveEventMarkerStatus => {
  const nowTimestamp = now.getTime();
  const startTimestamp = toTimestamp(event.start_time);
  const endTimestamp = toTimestamp(event.end_time);

  if (startTimestamp !== null && nowTimestamp < startTimestamp) return "upcoming";
  if (endTimestamp !== null && nowTimestamp >= endTimestamp) return "completed";

  if (startTimestamp !== null && nowTimestamp >= startTimestamp) {
    if (endTimestamp !== null && endTimestamp - nowTimestamp <= ENDING_SOON_WINDOW_MS) {
      return "endingSoon";
    }

    return "active";
  }

  return toFallbackStatus(event.status);
};

const getMarkerStatusForEventFilter = (eventStatus: EventStatus): LiveEventMarkerStatus | null => {
  switch (eventStatus) {
    case EventStatus.Active:
      return "active";
    case EventStatus.StartingSoon:
      return "upcoming";
    case EventStatus.EndingSoon:
      return "endingSoon";
    default:
      return null;
  }
};

export const matchesLiveEventStatusFilter = (
  event: LiveEventTiming,
  eventStatus: EventStatus,
  now: Date = new Date(),
): boolean => {
  const markerStatus = getMarkerStatusForEventFilter(eventStatus);

  return markerStatus !== null && getLiveEventMarkerStatus(event, now) === markerStatus;
};
